import { useEffect, useState } from 'react';
import { ENDPOINTS } from 'features/message-center/constants';
import { useShowAlert } from 'hooks';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { UIStatus } from 'UI/constants/status';

const COPIES = strings.messageCenter.messageComposer.templates;

export const useMessageTemplates = ({ onSelectTemplate }) => {
  const { showAlert } = useShowAlert();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const canUseTemplates = checkIfFeatureFlagEnabled(FeatureFlags.MessageCenterTemplates);

  const { state, refreshData } = useFetchWithStatusV2({
    endpoint: canUseTemplates ? ENDPOINTS.templates : null,
    apiInstance: SECONDARY_API_CLIENT
  });
  const responseResults = state.results || [];

  const [templates, setTemplates] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [uiState, setUiState] = useState({
    showSearchBar: true,
    showSkeleton: canUseTemplates
  });

  useEffect(() => {
    if (state.status === UIStatus.Success) {
      setTemplates(responseResults);
      setUiState(prev => ({ ...prev, showSkeleton: false, showSearchBar: true }));
    }
  }, [responseResults, state.status]);

  const handleSelectTemplate = newText => {
    onSelectTemplate(newText);
  };

  const handleClickNewTemplate = () => {
    if (templates.length > 0 && templates.find(template => !template.title)) return;

    setUiState(prev => ({ ...prev, showSearchBar: false }));
    const newTemplate = {
      id: null,
      title: '',
      template: ''
    };
    const updatedTemplates = [newTemplate, ...templates];
    setTemplates(updatedTemplates);
  };

  const updateTemplates = () => {
    setUiState(prev => ({ ...prev, showSkeleton: true }));
    refreshData();
  };

  const removeTemplate = index => {
    setUiState(prev => ({ ...prev, showSearchBar: true }));
    const updatedTemplates = templates.filter((template, templateIndex) => templateIndex !== index);
    setTemplates(updatedTemplates);
  };

  const handleSearchChange = (_name, value) => {
    setSearchKeyword(value);
    setUiState(prev => ({ ...prev, showSkeleton: true }));
  };

  const handleSearchAsync = async () => {
    const term = searchKeyword.trim();

    if (!term) {
      refreshData();
      return;
    }

    try {
      setTemplates([]);
      const response = await SECONDARY_API_CLIENT.get(`${ENDPOINTS.searchTemplates}?term=${term}`);
      setTemplates(response.data);
    } catch (error) {
      showAlert({
        title: strings.formatString(COPIES.alerts.error, { action: 'searching' }),
        error
      });
      refreshData();
    } finally {
      setUiState(prev => ({ ...prev, showSkeleton: false }));
    }
  };

  return {
    handleClickNewTemplate,
    handleSearchAsync,
    handleSearchChange,
    handleSelectTemplate,
    removeTemplate,
    searchKeyword,
    templates,
    updateTemplates,
    ...uiState
  };
};
